import React, { useState, useEffect, useRef } from 'react';

interface Product {
    id: string | number;
    name: string;
    sku?: string;
    stock_quantity: number;
    selling_price?: number;
    purchase_price?: number;
    description?: string;
}

interface ProductSelectProps {
    value: string | number;
    onChange: (id: string | number, product: Product) => void;
    products: Product[];
    disabled?: boolean;
    error?: boolean;
}

function ProductSelect({ value, onChange, products = [], disabled = false, error = false }: ProductSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState<Product[]>(products);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const selectedProduct = products.find(p => p && String(p.id) === String(value));

    // Handle outside click
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [wrapperRef]);

    // Update filtered products when search changes
    useEffect(() => {
        if (!searchTerm) {
            setFilteredProducts(products);
            return;
        }
        const lower = searchTerm.toLowerCase();
        setFilteredProducts(products.filter(p =>
            p && p.name && p.name.toLowerCase().includes(lower) ||
            (p && p.sku && p.sku.toLowerCase().includes(lower))
        ));
    }, [searchTerm, products]);

    // Focus input when opening
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen]);

    const handleSelect = (product: Product) => {
        onChange(product.id, product); // Pass ID and object
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className="relative w-full" ref={wrapperRef}>
            {/* Trigger Button */}
            <div
                className={`w-full px-3 py-2 bg-white border rounded-lg flex items-center justify-between cursor-pointer transition-colors ${error ? 'border-rose-300 ring-1 ring-rose-200' : 'border-slate-300 hover:border-indigo-400'
                    } ${disabled ? 'bg-slate-50 cursor-not-allowed text-slate-400' : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                {selectedProduct ? (
                    <div className="flex flex-col overflow-hidden">
                        <div className="font-medium text-slate-800 text-sm truncate">{selectedProduct.name}</div>
                        {selectedProduct.sku && (
                            <div className="text-xs text-slate-500 truncate">SKU: {selectedProduct.sku}</div>
                        )}
                    </div>
                ) : (
                    <span className="text-slate-400 text-sm">Select Product...</span>
                )}
                <svg className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            {/* Dropdown */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-80 overflow-hidden flex flex-col min-w-[300px]">
                    {/* Search Field */}
                    <div className="p-2 border-b border-slate-100">
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Search by name or SKU..."
                            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>

                    {/* List */}
                    <div className="overflow-y-auto flex-1">
                        {filteredProducts.length > 0 ? (
                            filteredProducts.map(product => (
                                <div
                                    key={product.id}
                                    onClick={() => handleSelect(product)}
                                    className={`px-3 py-3 hover:bg-indigo-50 cursor-pointer border-b border-slate-50 last:border-0 ${selectedProduct?.id === product.id ? 'bg-indigo-50/50' : ''
                                        }`}
                                >
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="flex-1">
                                            <div className="font-semibold text-slate-800 text-sm">{product.name}</div>
                                            <div className="text-xs text-slate-500 flex gap-2 mt-0.5">
                                                {product.sku && <span>SKU: {product.sku}</span>}
                                                <span>•</span>
                                                <span className={product.stock_quantity > 0 ? "text-emerald-600" : "text-rose-600"}>
                                                    Stock: {product.stock_quantity}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="text-right flex flex-col items-end">
                                            <div className="font-bold text-indigo-600 text-sm">Sell: ₹{parseFloat((product.selling_price as any) || 0).toFixed(2)}</div>
                                            <div className="font-medium text-slate-400 text-[10px] mt-0.5">Buy: ₹{parseFloat((product.purchase_price as any) || 0).toFixed(2)}</div>
                                        </div>
                                    </div>
                                    {product.description && (
                                        <div className="text-xs text-slate-400 mt-1 line-clamp-1">
                                            {product.description}
                                        </div>
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="p-4 text-center text-slate-500 text-sm">
                                No products found.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ProductSelect;
